import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Download,
  Euro,
  ExternalLink,
  Loader2,
  ShoppingBag,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  exportLoSalesCsv,
  fetchLoSellerSales,
  type LoSellerSaleRow,
  type LoSellerSalesPeriod,
  type LoSellerSalesResponse,
} from '@/lib/lyrraone-hub';
import { LyrraOnePlanGatedAction } from '@/components/lyrraone/LyrraOnePlanGatedAction';
import { useIsMobile } from '@/hooks/useMediaQuery';

const PERIODS: LoSellerSalesPeriod[] = ['7d', '30d', '90d', '365d', 'all'];

function formatEuros(cents: number, locale: string): string {
  return (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });
}

function buyerLabel(buyer: LoSellerSaleRow['buyer']): string {
  const name = [buyer.firstName, buyer.lastName].filter(Boolean).join(' ');
  return name || buyer.email;
}

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: React.ElementType;
  accent: string;
}) {
  return (
    <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{label}</p>
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', accent)}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
      <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{value}</p>
      {hint ? <p className="text-xs text-[#5B6472]/80 dark:text-[#B6CAEC]/80">{hint}</p> : null}
    </div>
  );
}

export default function LyrraOneSellerSalesPanel() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const [period, setPeriod] = useState<LoSellerSalesPeriod>('30d');
  const [data, setData] = useState<LoSellerSalesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const periodLabels = useMemo(
    () =>
      ({
        '7d': t('institution.sales.period7d'),
        '30d': t('institution.sales.period30d'),
        '90d': t('institution.sales.period90d'),
        '365d': t('institution.sales.period365d'),
        all: t('institution.sales.periodAll'),
      }) as Record<LoSellerSalesPeriod, string>,
    [t, i18n.language],
  );

  const typeLabels = useMemo(
    () =>
      ({
        formation: t('institution.sales.typeFormation'),
        bundle: t('institution.sales.typeBundle'),
        podcast: t('institution.sales.typePodcast'),
      }) as Record<LoSellerSaleRow['type'], string>,
    [t, i18n.language],
  );

  const paymentLabels = useMemo(
    () =>
      ({
        stripe: t('institution.sales.paymentStripe'),
        lyrrapay: t('institution.sales.paymentLyrraPay'),
        other: t('institution.sales.paymentOther'),
      }) as Record<LoSellerSaleRow['paymentChannel'], string>,
    [t, i18n.language],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchLoSellerSales({ period, limit, offset }));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [period]);

  const summary = data?.summary;
  const sales = data?.sales ?? [];
  const total = data?.total ?? 0;
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  const paymentBanner = (() => {
    if (!summary) return null;
    const { effectiveMethod, stripeConnected, stripeChargesEnabled } = summary.paymentSettings;
    if (effectiveMethod === 'stripe') {
      return stripeConnected && stripeChargesEnabled
        ? t('institution.sales.paymentBannerStripeActive')
        : t('institution.sales.paymentBannerStripePending');
    }
    return t('institution.sales.paymentBannerLyrraPay');
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.sales.title')}</h1>
          <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">{t('institution.sales.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as LoSellerSalesPeriod)}
            className="rounded-lg border border-[#E8EDF5] dark:border-[#19335E] bg-white dark:bg-[#122644] px-3 py-2 text-sm text-[#0C1C38] dark:text-white"
            aria-label={t('institution.sales.periodLabel')}
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>{periodLabels[p]}</option>
            ))}
          </select>
          <LyrraOnePlanGatedAction feature="dataExport">
          <button
            type="button"
            disabled={exporting}
            onClick={() => {
              setExporting(true);
              void exportLoSalesCsv().finally(() => setExporting(false));
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-[#E8EDF5] dark:border-[#19335E] bg-white dark:bg-[#122644] px-3 py-2 text-sm font-medium text-[#0C1C38] dark:text-white hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50 disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {t('institution.sales.exportCsv')}
          </button>
          </LyrraOnePlanGatedAction>
        </div>
      </div>

      {summary ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label={t('institution.sales.kpiGross')}
            value={formatEuros(summary.totalGrossCents, dateLocale)}
            hint={t('institution.sales.kpiGrossHint', { count: summary.totalSales })}
            icon={ShoppingBag}
            accent="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          />
          <KpiCard
            label={t('institution.sales.kpiNet')}
            value={formatEuros(summary.totalNetCents, dateLocale)}
            hint={t('institution.sales.kpiNetHint')}
            icon={TrendingUp}
            accent="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          />
          <KpiCard
            label={t('institution.sales.kpiFees')}
            value={formatEuros(summary.totalPlatformFeeCents, dateLocale)}
            hint={t('institution.sales.kpiFeesHint')}
            icon={Euro}
            accent="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          />
          <KpiCard
            label={t('institution.sales.kpiStripe')}
            value={String(summary.byPayment.stripe.count)}
            hint={formatEuros(summary.byPayment.stripe.grossCents, dateLocale)}
            icon={CreditCard}
            accent="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
          />
        </div>
      ) : null}

      {paymentBanner ? (
        <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-[#0C1C38] dark:text-white">{t('institution.sales.paymentTitle')}</p>
            <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mt-1">{paymentBanner}</p>
            {summary ? (
              <p className="text-xs text-[#5B6472]/80 dark:text-[#B6CAEC]/80 mt-2">
                {t('institution.sales.paymentBreakdown', {
                  stripe: summary.byPayment.stripe.count,
                  lyrrapay: summary.byPayment.lyrrapay.count,
                })}
              </p>
            ) : null}
          </div>
          {!isMobile ? (
            <Link
              to="/marketing/payments"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
            >
              {t('institution.sales.configurePayment')}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </Link>
          ) : null}
        </div>
      ) : null}

      <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E8EDF5] dark:border-[#19335E] flex items-center justify-between">
          <h3 className="font-semibold text-[#0C1C38] dark:text-white text-sm">{t('institution.sales.tableTitle')}</h3>
          {total > 0 ? (
            <span className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">
              {t('institution.sales.pagination', { from: offset + 1, to: Math.min(offset + limit, total), total })}
            </span>
          ) : null}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8EDF5] dark:border-[#19335E] bg-[#F8FAFC] dark:bg-[#122644]/50">
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colDate')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colProduct')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colBuyer')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colAmount')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colFee')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colNet')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colPayment')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.sales.colAffiliate')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4FA] dark:divide-[#19335E]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : sales.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">
                    {t('institution.sales.empty')}
                  </td>
                </tr>
              ) : (
                sales.map((sale) => (
                  <tr key={`${sale.type}-${sale.id}`} className="hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/30 transition-colors">
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#B6CAEC] whitespace-nowrap">
                      {new Date(sale.createdAt).toLocaleString(dateLocale, {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0C1C38] dark:text-white">{sale.productTitle}</p>
                      <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{typeLabels[sale.type]}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[#0C1C38] dark:text-white">{buyerLabel(sale.buyer)}</p>
                      <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{sale.buyer.email}</p>
                    </td>
                    <td className="px-4 py-3 text-end font-medium text-[#0C1C38] dark:text-white whitespace-nowrap">
                      {formatEuros(sale.amount, dateLocale)}
                    </td>
                    <td className="px-4 py-3 text-end text-[#5B6472] dark:text-[#B6CAEC] whitespace-nowrap">
                      {sale.platformFee > 0 ? formatEuros(sale.platformFee, dateLocale) : '—'}
                    </td>
                    <td className="px-4 py-3 text-end font-medium text-green-700 dark:text-green-300 whitespace-nowrap">
                      {formatEuros(sale.netAmount, dateLocale)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                          sale.paymentChannel === 'stripe'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                        )}
                      >
                        {paymentLabels[sale.paymentChannel]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                      {sale.affiliate
                        ? t('institution.sales.affiliateLine', {
                            code: sale.affiliate.code,
                            amount: formatEuros(sale.affiliate.commissionCents, dateLocale),
                          })
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {total > limit ? (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#E8EDF5] dark:border-[#19335E]">
            <button
              type="button"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              className="rounded-lg px-3 py-1.5 text-sm text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50 disabled:opacity-40"
            >
              {t('institution.sales.prev')}
            </button>
            <button
              type="button"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + limit)}
              className="rounded-lg px-3 py-1.5 text-sm text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50 disabled:opacity-40"
            >
              {t('institution.sales.next')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
